import { SxProps, Theme } from '@mui/material';
import { Color } from 'shared/utils/colors';

/**
 * Shared label typography for report chips and segmented controls.
 * Declared as a plain object (validated via `satisfies`) so it can be spread into other
 * style objects — a `SxProps<Theme>` annotation would widen it to an array/function union.
 */
export const REPORT_SELECTABLE_CONTROL_LABEL_SX = {
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.43,
} satisfies SxProps<Theme>;

/** MD3-style segmented controls in the report dialog — outlined group, flat segments. */
export const REPORT_SEGMENTED_CONTROL_GROUP_SX: SxProps<Theme> = {
  mr: 0,
  flexShrink: 0,
  border: 1,
  borderColor: Color.border,
  borderRadius: '8px',
  overflow: 'hidden',
  bgcolor: 'transparent',
  boxShadow: 'none',
  '& .MuiToggleButtonGroup-grouped': {
    flex: '0 0 auto',
    width: 'auto',
    minWidth: 'unset',
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    textTransform: 'none',
    ...REPORT_SELECTABLE_CONTROL_LABEL_SX,
    px: 1.5,
    py: 0.75,
    color: 'text.primary',
    boxShadow: 'none',
    transition: 'background-color 0.2s ease',
    '&:not(:last-of-type)': {
      borderRight: 1,
      borderColor: Color.border,
    },
    '&:hover': {
      backgroundColor: Color.scrimBlack06,
    },
    '&.Mui-selected': {
      backgroundColor: Color.primaryOrangePale,
      color: 'text.primary',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: Color.primaryOrangePale,
      },
    },
  },
};

export const REPORT_SEGMENTED_CONTROL_WRAPPER_SX: SxProps<Theme> = {
  maxWidth: '100%',
  minWidth: 0,
};

export const getReportSegmentedControlWrapperSx = (
  disabled?: boolean
): SxProps<Theme> => (theme) => ({
  ...REPORT_SEGMENTED_CONTROL_WRAPPER_SX,
  opacity: disabled ? theme.palette.action.disabledOpacity : 1,
  pointerEvents: disabled ? 'none' : undefined,
});

/** Shared label for display-style and scope segmented controls. */
export const REPORT_SEGMENTED_CONTROL_LABEL_SX: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  whiteSpace: 'nowrap',
};
